import { computed, signal, Signal } from '@angular/core';

import type { GridsterConfig } from 'angular-gridster2';

import { createProviderToken } from '@core/common-legacy';
import { ReportDashboardGridsterBaseConfig } from '@core/reports-types';

/** The minimal shape of each grid item. */
export interface ReportsGridItem {
  id: string;
  x: number;
  y: number;
  cols: number;
  rows: number;
  data?: unknown;
}

/** Partial overrides for the core GridsterConfig. */
export type ReportsGridConfig = Partial<GridsterConfig>;

// Token for the items signal
const { provide: provideGridItems, inject: injectGridItems } =
  createProviderToken<Signal<ReportsGridItem[]>>('reportsProvideGridItems')();

// Token for the config signal
const { provide: provideGridConfig, inject: injectGridConfig } = createProviderToken<
  Signal<ReportsGridConfig>
>('reportsProvideGridConfig')();

export const ReportsDashboardGridProviders = {
  provideGridItems,
  provideGridConfig,
};

export const ReportsDashboardGridInjectors = {
  injectGridItems,
  injectGridConfig,
};

/**
 * Build providers for items() and config().
 * - items: whenever opts.items() is provided, we register that factory directly.
 * - config: we always wrap base+overrides in a computed()
 */
export function reportsDashboardGridProviderFactory(opts: {
  items?: () => Signal<ReportsGridItem[]>;
  config?: () => Signal<Partial<GridsterConfig>>;
}): Array<ReturnType<typeof provideGridItems> | ReturnType<typeof provideGridConfig>> {
  const providers: any[] = [];

  if (opts.items) {
    // Wire up the domain’s items signal directly
    providers.push(provideGridItems({ useFactory: opts.items }));
  } else {
    // Fallback empty list if no items() given
    providers.push(provideGridItems({ useValue: computed(() => []) }));
  }

  if (opts.config) {
    // User supplied overrides → merge with base
    const overrideSignal = opts.config();
    const mergedConfig = computed<GridsterConfig>(() => ({
      ...ReportDashboardGridsterBaseConfig,
      ...overrideSignal(),
    }));
    providers.push(provideGridConfig({ useValue: mergedConfig }));
  } else {
    // No overrides → just base
    const mergedConfig = computed<GridsterConfig>(() => ReportDashboardGridsterBaseConfig);
    providers.push(provideGridConfig({ useValue: mergedConfig }));
  }

  return providers;
}

/**
 * A built-in fallback so the core component never errors if a domain forgets.
 * You can import this array directly into `viewProviders` in the component.
 * Always‑on defaults for every host */

const CORE_DEFAULT_GRID_CONFIG: ReportsGridConfig = {
  // pull in all of your base settings (margins, gridType, etc)
  ...ReportDashboardGridsterBaseConfig,

  // exactly original draggable config
  draggable: {
    delayStart: 0,
    enabled: true,
    ignoreContent: false,
    dropOverItems: false,
  },

  // exactly original resizable config
  resizable: {
    enabled: true,
  },
};

export const ReportsDashboardGridProvidersArrayForCoreFallback =
  reportsDashboardGridProviderFactory({
    items: () => signal([]),
    config: () => signal<ReportsGridConfig>(CORE_DEFAULT_GRID_CONFIG),
  });
